import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Card,
  Row,
  Col,
  Modal,
  Space,
  Typography,
  Tooltip,
  Popconfirm,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { apiGetPermissions, apiDeletePermission } from '@services/ManageRoles/RoleServices';
import { showSuccess, showError } from '@components/notification';
import PermissionFormModal from './PermissionFormModal';

const { Title } = Typography;

export default function ManagePermissions() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiGetPermissions({
        page: currentPage,
        size: pageSize,
        keyword: keyword || undefined
      });

      if (response?.data?.success !== false) {
        const permissions = response.data?.data || [];
        setData(
          permissions.map((item, index) => ({
            ...item,
            key: item.id,
            stt: (currentPage - 1) * pageSize + index + 1
          }))
        );
        setTotalItems(response.data?.meta?.total || 0);
      } else {
        setData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Lỗi khi tải danh sách permissions:', error);
      showError(error?.message || 'Không thể tải danh sách quyền hạn');
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  const handleClear = () => {
    setKeyword('');
    setCurrentPage(1);
    setTimeout(fetchData, 100);
  };

  const handleDelete = async (id) => {
    try {
      await apiDeletePermission(id);
      showSuccess({ message: 'Xóa quyền hạn thành công!' });
      fetchData();
    } catch (error) {
      showError(error?.message || 'Xóa quyền hạn thất bại');
    }
  };

  const handleEdit = (record) => {
    setEditingPermission(record);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingPermission(null);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 70,
      align: 'center'
    },
    {
      title: 'Tên quyền',
      dataIndex: 'name',
      render: (text) => (
        <Typography.Text strong style={{ color: '#0051A9' }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      render: (text) => text || 'Không có mô tả'
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      width: 120,
      align: 'center',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Hoạt động' : 'Không hoạt động'}</Tag>
      )
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      width: 150,
      render: (text) => (text ? new Date(text).toLocaleString('vi-VN') : '')
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{
                marginRight: 8,
                backgroundColor: '#1890ff',
                borderColor: '#1890ff'
              }}
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa quyền hạn này?"
              onConfirm={() => handleDelete(record.id)}
              okText="Xóa"
              cancelText="Hủy">
              <Button danger icon={<DeleteOutlined />} size="small" style={{ marginRight: 8 }} />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ color: '#0051A9', margin: 0 }}>
            Quản lý quyền hạn
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ backgroundColor: '#0051A9', borderColor: '#0051A9' }}>
            Thêm quyền hạn
          </Button>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={16}>
            <Input
              placeholder="Tìm kiếm theo tên quyền hoặc mô tả..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              suffix={
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  size="small"
                  onClick={handleSearch}
                  style={{ backgroundColor: '#0051A9', borderColor: '#0051A9', border: 'none' }}
                />
              }
            />
          </Col>
          <Col xs={24} sm={8}>
            <Space>
              <Button onClick={handleClear}>Xóa bộ lọc</Button>
            </Space>
          </Col>
        </Row>
      </Card>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        bordered
        scroll={{ x: 'max-content' }}
      />

      <PermissionFormModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPermission(null);
        }}
        editingPermission={editingPermission}
        onSuccess={fetchData}
      />
    </div>
  );
}
